import dayjs from 'dayjs';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { getRequest, postRequest } from '../../services/api';
import { resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { devError } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import { formatTime } from '../../utils/time';
import { type CoinType } from '../cointype/CoinTypeStore';
import type {
  FeatureFetchStatus,
  FeatureFetchType,
  FeatureManagerDialogType,
  FeatureStartTrigger,
  IFeatureManagerStore,
} from '../feature-manager/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog, type ClaimableReward } from '../ui/types';

import type {
  MissionSetResponse,
  GetUserMissionsResponse,
  ClaimedReward,
  MissionResponse,
  XpGiftCelebration,
  StreakFly,
} from './types';

const MISSIONS_DIALOG_EXIT_MS = 240;
const XP_GIFT_PLAY_DELAY_MS = 144;
const XP_GIFT_MAX_MS = 5000;

const grandPrizeShownKey = (coinType: CoinType, setId: string) => `missions_gp_shown_${coinType}_${setId}`;

export class MissionsStore implements IFeatureManagerStore {
  private root: RootStore;

  constructor(root: RootStore) {
    this.root = root;
    makeObservable(this);
  }

  dialogTypes: FeatureManagerDialogType[] = ['Missions'];

  @observable scMissions: MissionSetResponse | null = null;
  @observable gcMissions: MissionSetResponse | null = null;

  // Onboarding (MZ-488): `onboardingTransitionConsumed` latches the one-shot unlock UI off after it plays.
  @observable isOnboarding = false;
  @observable onboardingMissions: MissionSetResponse | null = null;
  @observable onboardingTransitionConsumed = false;

  @observable isDialogVisible = false;
  @observable isLoading = false;
  @observable scTimer = 0;
  @observable gcTimer = 0;
  @observable scNextSetTimer = 0;
  @observable gcNextSetTimer = 0;

  public activeMissionShowed = false;

  // Signature of the completed-mission set last celebrated by the lobby progress bar.
  // Lives on the store so the animation survives the lobby unmounting while the player is in a game,
  // and replays only when the set of completed missions actually changes. Observable so the
  // `hasPendingCelebration` computed reacts when the bar updates it.
  @observable lastCelebratedKey = '';

  @observable scGrandPrizeAnimationShown = false;
  @observable gcGrandPrizeAnimationShown = false;

  @observable xpGift: XpGiftCelebration | null = null;
  private xpGiftTimeout: ReturnType<typeof setTimeout> | null = null;

  @observable streakFlies: StreakFly[] = [];
  private streakFlyId = 0;

  @observable streakFillPoints = 0;

  @observable isDialogClosing = false;
  private dialogCloseTimeout: ReturnType<typeof setTimeout> | null = null;

  @computed
  get currentMissions(): MissionSetResponse | null {
    return this.root.coinTypeStore.coinType === 'GC' ? this.gcMissions : this.scMissions;
  }

  @computed
  get activeMission(): MissionResponse | null {
    return this.currentMissions?.missions.find((m) => m.status === 'active') ?? null;
  }

  @computed
  get onboardingActiveMission(): MissionResponse | null {
    const missions = this.onboardingMissions?.missions ?? [];
    return missions.find((m) => m.status === 'active') ?? missions.find((m) => m.status === 'completed') ?? null;
  }

  // The one-shot unlock read: play the animation once, then consume. Requires a non-empty chain —
  // an empty onboardingMissions must never suppress the regular bar or arm the transition.
  @computed
  get isOnboardingTransition(): boolean {
    return (
      !this.isOnboarding && (this.onboardingMissions?.missions.length ?? 0) > 0 && !this.onboardingTransitionConsumed
    );
  }

  @computed
  get showOnboardingBar(): boolean {
    return (this.isOnboarding && this.onboardingActiveMission !== null) || this.isOnboardingTransition;
  }

  @computed
  get hasClaimable(): boolean {
    return this.currentMissions?.missions.some((m) => m.status === 'completed') ?? false;
  }

  @computed
  get claimableCount(): number {
    return this.currentMissions?.missions.filter((m) => m.status === 'completed').length ?? 0;
  }

  @computed
  get hasOldRewards(): boolean {
    return this.currentMissions?.unclaimedRewards != null;
  }

  @computed
  get unclaimedSetId(): string | null {
    return this.currentMissions?.unclaimedRewards?.setId ?? null;
  }

  @computed
  get isActiveMissionGrandPrize(): boolean {
    const missions = [...(this.currentMissions?.missions ?? [])].sort((a, b) => b.order - a.order);
    const grandPrizeMission = missions[0];
    return this.activeMission?.id === grandPrizeMission?.id;
  }

  // The previous set's full chain, shown sorted like the regular list.
  // Old rewards aren't playable anymore, so any active mission is shown locked (upcoming).
  @computed
  get unclaimedMissions(): MissionResponse[] {
    return (this.currentMissions?.unclaimedRewards?.missions ?? []).map((mission) =>
      mission.status === 'active' ? { ...mission, status: 'upcoming' } : mission,
    );
  }

  // Completed missions the lobby progress bar celebrates with a fill-to-end (old rewards first).
  @computed
  get completedToCelebrate(): MissionResponse[] {
    return this.hasOldRewards
      ? this.unclaimedMissions.filter((m) => m.status === 'completed')
      : (this.currentMissions?.missions ?? []).filter((m) => m.status === 'completed');
  }

  // Signature of the completed set; the bar replays its animation only when this changes.
  @computed
  get celebrateKey(): string {
    return `${this.hasOldRewards ? 'old' : 'cur'}:${this.completedToCelebrate.map((m) => m.id).join(',')}`;
  }

  // True while the lobby progress bar still owes a fill animation for the current completed set.
  // The bar opens the dialog itself once that animation finishes, so auto-open should defer to it.
  @computed
  get hasPendingCelebration(): boolean {
    return this.completedToCelebrate.length > 0 && this.celebrateKey !== this.lastCelebratedKey;
  }

  @computed
  get upcomingMissionStartAt(): string | null {
    const coinType = this.root.coinTypeStore.coinType;
    const missions = coinType === 'GC' ? this.gcMissions : this.scMissions;
    return missions?.nextMissionsStartAt ?? null;
  }

  @computed
  get grandPrizeAnimationShown(): boolean {
    return this.root.coinTypeStore.coinType === 'GC'
      ? this.gcGrandPrizeAnimationShown
      : this.scGrandPrizeAnimationShown;
  }

  // True when the backend asked to play the grand-prize reveal for the current set and it hasn't
  // played yet (plays only once; `grandPrizeAnimationShown` is set when the row starts it).
  @computed
  get shouldPlayGrandPrizeReveal(): boolean {
    return !!this.currentMissions?.playGrandPrizeAnimation && !this.grandPrizeAnimationShown;
  }

  @action setLastCelebratedKey(key: string) {
    this.lastCelebratedKey = key;
  }

  @action consumeOnboardingTransition() {
    this.onboardingTransitionConsumed = true;
  }

  @action setIsLoading(v: boolean) {
    this.isLoading = v;
  }
  @action setIsDialogVisible(v: boolean) {
    this.isDialogVisible = v;
  }
  @action setTimer(value: number, coinType: CoinType) {
    if (coinType === 'GC') {
      this.gcTimer = value;
    } else {
      this.scTimer = value;
    }
  }

  @action setNextSetTimer(value: number, coinType: CoinType) {
    if (coinType === 'GC') {
      this.gcNextSetTimer = value;
    } else {
      this.scNextSetTimer = value;
    }
  }

  @action setGrandPrizeAnimationShown(shown = true) {
    const coinType = this.root.coinTypeStore.coinType;
    if (coinType === 'GC') this.gcGrandPrizeAnimationShown = shown;
    else this.scGrandPrizeAnimationShown = shown;

    const setId = this.currentMissions?.missions[0]?.setId;
    if (setId) {
      try {
        if (shown) localStorage.setItem(grandPrizeShownKey(coinType, setId), '1');
        else localStorage.removeItem(grandPrizeShownKey(coinType, setId));
      } catch {}
    }
  }

  private syncGrandPrizeAnimationShown(
    coinType: CoinType,
    prevSetId: string | undefined,
    nextSetId: string | undefined,
  ) {
    const shown = coinType === 'GC' ? this.gcGrandPrizeAnimationShown : this.scGrandPrizeAnimationShown;
    if (nextSetId !== prevSetId) {
      // New set — clear the flag and any stale localStorage entry.
      if (coinType === 'GC') this.gcGrandPrizeAnimationShown = false;
      else this.scGrandPrizeAnimationShown = false;
      if (prevSetId) {
        try {
          localStorage.removeItem(grandPrizeShownKey(coinType, prevSetId));
        } catch {}
      }
    } else if (nextSetId && !shown) {
      // Same set on re-fetch (e.g. page refresh) — restore from localStorage.
      try {
        const restored = localStorage.getItem(grandPrizeShownKey(coinType, nextSetId)) === '1';
        if (coinType === 'GC') this.gcGrandPrizeAnimationShown = restored;
        else this.scGrandPrizeAnimationShown = restored;
      } catch {}
    }
  }

  public getTimer(coinType: CoinType): string | null {
    const timer = this.getRawTimer(coinType);
    return timer > 0 ? formatTime(timer, 's') : null;
  }

  public getStreakExpirationTimer(): string | null {
    const streak = this.currentMissions?.streak;
    if (!streak) return null;
    const timer = streak.expiresAt ? Math.max(0, dayjs(streak.expiresAt).diff(dayjs(), 'seconds')) : 0;
    return timer > 0 ? formatTime(timer, 's') : null;
  }

  public getRawUpcomingMissionStartAt(coinType: CoinType): number {
    return coinType === 'GC' ? this.gcNextSetTimer : this.scNextSetTimer;
  }

  public getRawTimer(coinType: CoinType): number {
    return coinType === 'GC' ? this.gcTimer : this.scTimer;
  }

  private scInterval: ReturnType<typeof setInterval> | null = null;
  private gcInterval: ReturnType<typeof setInterval> | null = null;
  private scNextSetInterval: ReturnType<typeof setInterval> | null = null;
  private gcNextSetInterval: ReturnType<typeof setInterval> | null = null;
  // Fires when the next mission set starts (the empty/waiting state has no per-second timer running).
  private nextSetTimeout: ReturnType<typeof setTimeout> | null = null;

  private seedTimers(sc: MissionSetResponse, gc: MissionSetResponse) {
    const scMission =
      sc.missions.find((m) => m.status === 'active') ?? sc.missions.find((m) => m.status === 'upcoming') ?? null;
    const gcMission =
      gc.missions.find((m) => m.status === 'active') ?? gc.missions.find((m) => m.status === 'upcoming') ?? null;

    if (this.scInterval) {
      clearInterval(this.scInterval);
      this.scInterval = null;
    }
    if (this.scNextSetInterval) {
      clearInterval(this.scNextSetInterval);
      this.scNextSetInterval = null;
    }

    if (scMission && scMission.timeLeft > 0) {
      this.setTimer(scMission.timeLeft, 'SC');
      this.setNextSetTimer(0, 'SC');
      this.scInterval = setInterval(() => {
        if (this.scTimer > 0) {
          this.setTimer(this.scTimer - 1, 'SC');
        } else {
          clearInterval(this.scInterval!);
          this.scInterval = null;
          void this.fetch()
            .then(() => {
              this.root.featureManagerStore.addDialogsToSequenceQueue(['Missions']);
            })
            .catch((e) => devError('MissionsStore.seedTimers SC', e));
        }
      }, 1000);
    } else {
      this.setTimer(0, 'SC');
      const scSecondsUntilNext = sc.nextMissionsStartAt
        ? Math.max(0, dayjs(sc.nextMissionsStartAt).diff(dayjs(), 'seconds'))
        : 0;
      this.setNextSetTimer(scSecondsUntilNext, 'SC');
      if (scSecondsUntilNext > 0) {
        this.scNextSetInterval = setInterval(() => {
          if (this.scNextSetTimer > 0) {
            this.setNextSetTimer(this.scNextSetTimer - 1, 'SC');
          } else {
            clearInterval(this.scNextSetInterval!);
            this.scNextSetInterval = null;
          }
        }, 1000);
      }
    }

    if (this.gcInterval) {
      clearInterval(this.gcInterval);
      this.gcInterval = null;
    }
    if (this.gcNextSetInterval) {
      clearInterval(this.gcNextSetInterval);
      this.gcNextSetInterval = null;
    }

    if (gcMission && gcMission.timeLeft > 0) {
      this.setTimer(gcMission.timeLeft, 'GC');
      this.setNextSetTimer(0, 'GC');
      this.gcInterval = setInterval(() => {
        if (this.gcTimer > 0) {
          this.setTimer(this.gcTimer - 1, 'GC');
        } else {
          clearInterval(this.gcInterval!);
          this.gcInterval = null;
          void this.fetch()
            .then(() => {
              this.root.featureManagerStore.addDialogsToSequenceQueue(['Missions']);
            })
            .catch((e) => devError('MissionsStore.seedTimers GC', e));
        }
      }, 1000);
    } else {
      this.setTimer(0, 'GC');
      const gcSecondsUntilNext = gc.nextMissionsStartAt
        ? Math.max(0, dayjs(gc.nextMissionsStartAt).diff(dayjs(), 'seconds'))
        : 0;
      this.setNextSetTimer(gcSecondsUntilNext, 'GC');
      if (gcSecondsUntilNext > 0) {
        this.gcNextSetInterval = setInterval(() => {
          if (this.gcNextSetTimer > 0) {
            this.setNextSetTimer(this.gcNextSetTimer - 1, 'GC');
          } else {
            clearInterval(this.gcNextSetInterval!);
            this.gcNextSetInterval = null;
          }
        }, 1000);
      }
    }

    this.scheduleNextSetRefetch(sc, gc);
  }

  // When the current set is over (no active-mission timer running), refetch once the next set starts.
  private scheduleNextSetRefetch(sc: MissionSetResponse, gc: MissionSetResponse) {
    if (this.nextSetTimeout) {
      clearTimeout(this.nextSetTimeout);
      this.nextSetTimeout = null;
    }

    const now = Date.now();
    const nextStarts = [sc.nextMissionsStartAt, gc.nextMissionsStartAt]
      .filter((t): t is string => !!t)
      .map((t) => new Date(t).getTime())
      .filter((ms) => ms > now);
    if (nextStarts.length === 0) return;

    this.nextSetTimeout = setTimeout(
      () => {
        this.nextSetTimeout = null;
        void this.fetch()
          .then(() => this.root.featureManagerStore.addDialogsToSequenceQueue(['Missions']))
          .catch((e) => devError('MissionsStore.scheduleNextSetRefetch', e));
      },
      Math.min(...nextStarts) - now,
    );
  }

  public fetch = singleFlight(async (_options?: { fetchType: FeatureFetchType }): Promise<FeatureFetchStatus> => {
    try {
      const featureFlag = resolveFlag('missions_v2_enabled', false);
      const frontendFlag = resolveFlag('missions_v2_frontend_enabled', false);
      const missionsFeEnabled = featureFlag;

      const missionsEnabled = missionsFeEnabled && frontendFlag;
      if (!missionsEnabled) return 'OFF';

      const response = await getRequest<ApiResponse<GetUserMissionsResponse>>('/v2/missions');
      if (response?.data?.data) {
        const { scMissions, gcMissions, isOnboarding, onboardingMissions } = response.data.data;
        runInAction(() => {
          const prevScSetId = this.scMissions?.missions[0]?.setId;
          const prevGcSetId = this.gcMissions?.missions[0]?.setId;
          this.scMissions = scMissions;
          this.gcMissions = gcMissions;
          // new read = new server truth; reset the one-shot latch
          this.isOnboarding = isOnboarding ?? false;
          this.onboardingMissions = onboardingMissions ?? null;
          this.onboardingTransitionConsumed = false;
          this.activeMissionShowed = false;
          this.streakFillPoints = 0;
          // Only reset the animation flag when the mission set actually changes.
          // Routine refetches (timer expiry, claim) on the same set must not replay the reveal.
          this.syncGrandPrizeAnimationShown('SC', prevScSetId, scMissions.missions[0]?.setId);
          this.syncGrandPrizeAnimationShown('GC', prevGcSetId, gcMissions.missions[0]?.setId);
        });
        this.seedTimers(scMissions, gcMissions);
        return 'READY';
      }
      return 'ERROR';
    } catch (error) {
      devError('MissionsStore.fetch', error);
      return 'ERROR';
    }
  });

  // setId force-claims a specific previous set; omit it to claim everything currently claimable.
  public async claim(coinType: CoinType, setId?: string): Promise<ClaimedReward[] | null> {
    this.setIsLoading(true);
    try {
      const response = await postRequest<ApiResponse<{ rewards: ClaimedReward[] }>>('/v2/missions/claim', {
        coinType,
        ...(setId ? { setId } : {}),
      });
      return response?.data?.data?.rewards ?? null;
    } catch (error) {
      devError('MissionsStore.claim', error);
      return null;
    } finally {
      this.setIsLoading(false);
    }
  }

  // After claiming a previous set, drop it locally for that coin type (no refetch needed).
  @action
  public clearUnclaimedRewards(coinType: CoinType) {
    const set = coinType === 'GC' ? this.gcMissions : this.scMissions;
    if (set) set.unclaimedRewards = null;
  }

  @action
  public reset() {
    if (this.scInterval) {
      clearInterval(this.scInterval);
      this.scInterval = null;
    }
    if (this.gcInterval) {
      clearInterval(this.gcInterval);
      this.gcInterval = null;
    }
    if (this.scNextSetInterval) {
      clearInterval(this.scNextSetInterval);
      this.scNextSetInterval = null;
    }
    if (this.gcNextSetInterval) {
      clearInterval(this.gcNextSetInterval);
      this.gcNextSetInterval = null;
    }
    if (this.nextSetTimeout) {
      clearTimeout(this.nextSetTimeout);
      this.nextSetTimeout = null;
    }
    this.scMissions = null;
    this.gcMissions = null;
    this.isOnboarding = false;
    this.onboardingMissions = null;
    this.onboardingTransitionConsumed = false;
    this.scTimer = 0;
    this.gcTimer = 0;
    this.isLoading = false;
    this.isDialogVisible = false;
    this.activeMissionShowed = false;
    this.lastCelebratedKey = '';
    this.scGrandPrizeAnimationShown = false;
    this.gcGrandPrizeAnimationShown = false;
    this.xpGift = null;
    if (this.xpGiftTimeout) {
      clearTimeout(this.xpGiftTimeout);
      this.xpGiftTimeout = null;
    }
    this.streakFlies = [];
    this.streakFillPoints = 0;
    if (this.dialogCloseTimeout) {
      clearTimeout(this.dialogCloseTimeout);
      this.dialogCloseTimeout = null;
    }
    this.isDialogClosing = false;
  }

  private autoOpenSeen(coinType: CoinType, setId: string): boolean {
    try {
      return localStorage.getItem(`missions_autoopen_${coinType}_${setId}`) === '1';
    } catch {
      return false;
    }
  }

  private markAutoOpenSeen(coinType: CoinType, setId: string): void {
    try {
      localStorage.setItem(`missions_autoopen_${coinType}_${setId}`, '1');
    } catch {}
  }

  public openDialog() {
    if (this.dialogCloseTimeout) {
      clearTimeout(this.dialogCloseTimeout);
      this.dialogCloseTimeout = null;
    }
    runInAction(() => {
      this.isDialogClosing = false;
    });
    this.root.uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenMissionsDialog });
    this.setIsDialogVisible(true);
  }

  public closeDialog() {
    this.root.uiStore.setCloseDialog();
    this.setIsDialogVisible(false);
    runInAction(() => {
      this.isDialogClosing = false;
    });
  }

  @action
  public closeDialogAnimated(onClosed?: () => void) {
    if (!this.isDialogVisible) {
      onClosed?.();
      return;
    }
    if (this.dialogCloseTimeout) clearTimeout(this.dialogCloseTimeout);
    this.isDialogClosing = true;
    this.dialogCloseTimeout = setTimeout(() => {
      this.dialogCloseTimeout = null;
      this.closeDialog();
      onClosed?.();
    }, MISSIONS_DIALOG_EXIT_MS);
  }

  @action
  public startXpGiftCelebration(payload: { boxNum: number; rewards: ClaimableReward[] }) {
    this.closeDialogAnimated(() => this.root.featureManagerStore.onDialogFinished('Missions'));
    this.xpGift = { ...payload, play: false };
    setTimeout(() => {
      runInAction(() => {
        if (this.xpGift) this.xpGift.play = true;
      });
    }, XP_GIFT_PLAY_DELAY_MS);
    if (this.xpGiftTimeout) clearTimeout(this.xpGiftTimeout);
    this.xpGiftTimeout = setTimeout(() => runInAction(() => this.clearXpGift()), XP_GIFT_MAX_MS);
  }

  @action
  public clearXpGift() {
    this.xpGift = null;
    if (this.xpGiftTimeout) {
      clearTimeout(this.xpGiftTimeout);
      this.xpGiftTimeout = null;
    }
  }

  @action
  public startStreakFly(payload: Omit<StreakFly, 'id'>) {
    this.streakFlies.push({ ...payload, id: this.streakFlyId++ });
  }

  @action
  public setStreakFillPoints(points: number) {
    this.streakFillPoints = points;
  }

  @action
  public endStreakFly(id: number) {
    this.streakFlies = this.streakFlies.filter((fly) => fly.id !== id);
  }

  public startDialogs(type?: FeatureManagerDialogType, _trigger?: FeatureStartTrigger): boolean {
    if (type === 'Missions') {
      const missionsEnabled =
        resolveFlag('missions_v2_enabled', false) && resolveFlag('missions_v2_frontend_enabled', false);

      if (!missionsEnabled) return false;

      // The onboarding bar owns the dialog open during the unlock transition (same slot-claim
      // pattern as hasPendingCelebration below) — otherwise the sequence opens the dialog over
      // the still-playing unlock animation. Placed before the GC gate: finalize opens the dialog
      // in either lobby.
      if (this.isOnboardingTransition) {
        this.activeMissionShowed = true;
        return true;
      }

      // Per-lobby auto-open switch (generic gate lives in FeatureManagerStore). Unclaimed rewards
      // must stay claimable in any coin type, hence the carve-out.
      const coinType = this.root.coinTypeStore.coinType;
      if (!this.root.featureManagerStore.isDialogLobbyEnabled('Missions', coinType) && !this.hasOldRewards) {
        return false;
      }

      // The lobby progress bar opens the dialog itself once its fill animation finishes. Claim the
      // feature-manager slot (without opening) so it waits for that dialog and continues in order
      // when it closes (via onDialogFinished), instead of being pre-empted here.
      if (this.hasPendingCelebration) {
        this.activeMissionShowed = true;
        return true;
      }

      if (!this.activeMissionShowed) {
        // Claim-pending always force-opens — never gated by the per-set marker.
        if (this.hasClaimable || this.hasOldRewards) {
          this.activeMissionShowed = true;
          this.openDialog();
          return true;
        }
        // Plain active-mission auto-open — once per set, per coin type.
        const setId = this.currentMissions?.missions[0]?.setId;
        const alreadySeen = setId ? this.autoOpenSeen(coinType, setId) : false;
        if (this.activeMission && setId && !alreadySeen) {
          this.activeMissionShowed = true;
          this.markAutoOpenSeen(coinType, setId);
          this.openDialog();
          return true;
        }
      }
    }

    return false;
  }
}
